import { useMemo, useCallback } from "react";
import { Trash2, Plus, Minus, ChevronLeft, Tag, Clock } from "lucide-react";
import { Button } from "@/app/components/button";
import type { Ad } from "@/app/components/ad-card";
import { BottomNav } from "./bottom-nav";
import { formatCurrency } from "@/config/locale";
import { AdImage } from "@/app/components/common/ad-image";
import { getAdImage } from "@/app/utils/ad-utils";

/** Coerce to string so we never render an object as React child (error #300). */
function safeText(x: unknown): string {
  if (x == null) return "";
  if (typeof x === "string" || typeof x === "number") return String(x);
  return "";
}

/** Trade-in product shown in offer details (product-plus-cash offers). */
export interface CartItemTradeIn {
  id: string;
  value: number;
  title: string;
  image: string;
}

export interface CartItem {
  ad: Ad;
  quantity: number;

  // Offer-specific fields
  offerId?: string;
  isOfferItem?: boolean;
  /** Amount the buyer pays (cash); used for cart total. */
  offerPrice?: number;
  offerExpiration?: Date;
  /** Products offered in swap (for "Detalhes da Oferta" in cart). */
  offerTradeInAds?: CartItemTradeIn[];
}

interface CartPageProps {
  onBack: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (adId: string, quantity: number) => void;
  onRemoveItem: (adId: string) => void;
  onProceedToCheckout: () => void;
  onPostAdClick?: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export function CartPage({
  onBack,
  cartItems,
  onUpdateQuantity: _onUpdateQuantity,
  onRemoveItem,
  onProceedToCheckout,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: CartPageProps) {
  // Memoize expensive calculations
  const { subtotal, total } = useMemo(() => {
    const subtotal = cartItems.reduce((sum, item) => sum + (item.offerPrice || item.ad.price) * item.quantity, 0);
    return { subtotal, total: subtotal };
  }, [cartItems]);

  // Helper to calculate time remaining for offer
  const getTimeRemaining = useCallback((expirationDate: Date) => {
    const now = new Date();
    const diff = expirationDate.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  }, []);

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white">Meu Carrinho</h1>
              <p className="text-sm text-white/80">
                {cartItems.length} {cartItems.length === 1 ? "item" : "itens"}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {cartItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-secondary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h3 className="mb-2">Seu carrinho está vazio</h3>
            <p className="text-muted-foreground mb-6">
              Adicione produtos para começar sua compra
            </p>
            <Button onClick={onBack}>
              Continuar Comprando
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Cart Items */}
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item.ad.id}
                  className={`bg-card border rounded-lg p-4 flex flex-col gap-3 ${
                    item.isOfferItem 
                      ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20' 
                      : 'border-border'
                  }`}
                >
                  {/* Offer Badge */}
                  {item.isOfferItem && (
                    <div className="flex items-center justify-between gap-2 pb-2 border-b border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-semibold text-green-600 uppercase">
                          Oferta Aceita
                        </span>
                      </div>
                      {item.offerExpiration && (
                        <div className="flex items-center gap-1 text-orange-600">
                          <Clock className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium">
                            Expira em {getTimeRemaining(item.offerExpiration)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Item Content */}
                  <div className="flex gap-4">
                    <AdImage
                      src={getAdImage(item.ad) || ""}
                      alt={safeText(item.ad?.title)}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm mb-1 line-clamp-2">
                        {safeText(item.ad?.title)}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        {safeText(item.ad?.category)}
                      </p>
                      
                      {/* Price - show strikethrough for offers */}
                      <div className="flex items-baseline gap-2">
                        {item.isOfferItem && Number(item.offerPrice) && Number(item.offerPrice) < Number(item.ad?.price) && (
                          <span className="text-xs text-muted-foreground line-through">
                            {formatCurrency(Number(item.ad?.price) || 0)}
                          </span>
                        )}
                        <p className={`font-semibold ${item.isOfferItem ? 'text-green-600' : 'text-primary'}`}>
                          {formatCurrency(Number(item.offerPrice) || Number(item.ad?.price) || 0)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => onRemoveItem(item.ad.id)}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                        aria-label="Remover do carrinho"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </button>
                      {/* Quantidade fixa em 1 por enquanto; reativar controles quando listagens permitirem >1 unidade. */}
                      <div
                        className="flex items-center gap-2 border rounded-lg border-border opacity-60"
                        aria-label={`Quantidade: ${safeText(item.quantity)} (alteração em breve)`}
                      >
                        <button
                          type="button"
                          className="p-2 cursor-not-allowed text-muted-foreground"
                          disabled
                          aria-hidden
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium text-muted-foreground tabular-nums">
                          {safeText(item.quantity)}
                        </span>
                        <button
                          type="button"
                          className="p-2 cursor-not-allowed text-muted-foreground"
                          disabled
                          aria-hidden
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Offer details: same layout as offer-detail screen — product(s) offered + cash + total */}
                  {item.isOfferItem && (
                    <div className="rounded-lg border border-border bg-muted/30 dark:bg-muted/10 p-3 space-y-3 text-sm">
                      <h4 className="font-semibold text-foreground">Detalhes da Oferta:</h4>
                      {item.offerTradeInAds && item.offerTradeInAds.length > 0 && (
                        <>
                          <p className="text-muted-foreground font-medium">Produto(s) oferecido(s):</p>
                          <ul className="space-y-2">
                            {item.offerTradeInAds.map((t) => (
                              <li key={t.id} className="flex items-center gap-3 rounded-lg bg-background/50 p-2">
                                <AdImage
                                  src={t.image || undefined}
                                  alt={safeText(t.title)}
                                  className="w-12 h-12 object-cover rounded shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-foreground truncate">{safeText(t.title)}</p>
                                  <p className="text-primary font-medium">{formatCurrency(Number(t.value) || 0)}</p>
                                </div>
                              </li>
                            ))}
                          </ul>
                          <div className="flex justify-between text-muted-foreground">
                            <span>Valor dos produtos:</span>
                            <span>{formatCurrency(item.offerTradeInAds.reduce((s, t) => s + (Number(t.value) || 0), 0))}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-muted-foreground">
                        <span>Pagamento em dinheiro:</span>
                        <span>{formatCurrency(Number(item.offerPrice) || 0)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-primary pt-1 border-t border-border">
                        <span>Valor total da oferta:</span>
                        <span>
                          {formatCurrency(
                            (item.offerTradeInAds?.reduce((s, t) => s + (Number(t.value) || 0), 0) ?? 0) + (Number(item.offerPrice) || 0)
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Offer Notice */}
                  {item.isOfferItem && (
                    <div className="text-xs text-green-700 dark:text-green-400 bg-green-100/50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
                      💡 Esta oferta foi aceita pelo vendedor. Complete o pagamento para garantir!
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="font-semibold text-lg mb-4">Resumo do Pedido</h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span className="text-primary">{formatCurrency(total)}</span>
                </div>
              </div>

              <Button
                onClick={onProceedToCheckout}
                className="w-full mt-6"
                size="lg"
              >
                Continuar para Entrega
              </Button>
            </div>
          </div>
        )}
      </div>

      <BottomNav
        activeTab="home"
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}